import { useState } from "react";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import api, { formatApiError } from "@/lib/api";
import { COMPANY } from "@/data/content";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ type: null, msg: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: "loading", msg: "" });
    try {
      await api.post("/contact", form);
      setStatus({ type: "success", msg: "Thank you. We'll respond within one working day." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({
        type: "error",
        msg: formatApiError(err?.response?.data?.detail) || "Could not submit. Please try again.",
      });
    }
  };

  return (
    <>
      <section className="pt-20 md:pt-24 pb-6 md:pb-10 max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14" data-testid="contact-hero">
        <div className="mono-label text-zinc-500">Contact</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] font-light mt-3 max-w-4xl">
          Let's <em className="not-italic text-[#BE1E2D] font-normal">build.</em>
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 pb-16 md:pb-24 grid lg:grid-cols-12 gap-8 lg:gap-12">
        {/* LEFT: info */}
        <div className="lg:col-span-5 border-t border-zinc-200 pt-6">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl leading-[1.05] tracking-tight">
              Abuja-based. Available nationwide.
            </h2>
            <p className="text-zinc-600 mt-4 leading-relaxed max-w-md text-[15px]">
              For tenders, proposals and partnership enquiries — our team responds within one working day.
            </p>

            <div className="mt-8 space-y-6">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#BE1E2D] mt-1 flex-shrink-0" />
                <div>
                  <div className="mono-label text-zinc-500">Office</div>
                  <div className="mt-1.5 text-zinc-800 leading-relaxed text-[15px]">{COMPANY.location}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#BE1E2D] mt-1 flex-shrink-0" />
                <div>
                  <div className="mono-label text-zinc-500">Phone</div>
                  <div className="mt-1.5 space-y-1">
                    {COMPANY.phones.map((p) => (
                      <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="block link-underline w-fit text-[15px]">
                        {p}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#BE1E2D] mt-1 flex-shrink-0" />
                <div>
                  <div className="mono-label text-zinc-500">Email</div>
                  <div className="mt-1.5 space-y-1">
                    {COMPANY.emails.map((e) => (
                      <a key={e} href={`mailto:${e}`} className="block link-underline w-fit text-[15px]">
                        {e}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* RIGHT: form */}
        <div className="lg:col-span-6 lg:col-start-7 border-t border-zinc-200 pt-6">
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Full Name" required>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="contact-input"
                  data-testid="contact-input-name"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Email" required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="contact-input"
                  data-testid="contact-input-email"
                  placeholder="jane@company.com"
                />
              </Field>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Phone">
                <input
                  value={form.phone}
                  onChange={update("phone")}
                  className="contact-input"
                  data-testid="contact-input-phone"
                  placeholder="+234 …"
                />
              </Field>
              <Field label="Subject">
                <input
                  value={form.subject}
                  onChange={update("subject")}
                  className="contact-input"
                  data-testid="contact-input-subject"
                  placeholder="Project inquiry"
                />
              </Field>
            </div>
            <Field label="Message" required>
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={update("message")}
                className="contact-input resize-none"
                data-testid="contact-input-message"
                placeholder="Tell us about your project, scope and timeline…"
              />
            </Field>

            <div className="flex items-center gap-4 pt-2">
              <button
                type="submit"
                disabled={status.type === "loading"}
                className="btn-primary"
                data-testid="contact-submit"
              >
                {status.type === "loading" ? "Sending…" : "Send Message"}
                <Send className="w-4 h-4" />
              </button>
              {status.type === "success" && (
                <div className="mono-label text-emerald-700" data-testid="contact-success">
                  ✓ {status.msg}
                </div>
              )}
              {status.type === "error" && (
                <div className="mono-label text-red-700" data-testid="contact-error">
                  ✕ {status.msg}
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      <style>{`
        .contact-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid #E4E4E7;
          padding: 12px 0;
          font-size: 16px;
          outline: none;
          transition: border-color 0.2s ease;
          font-family: Manrope, system-ui, sans-serif;
          color: #0A0A0A;
        }
        .contact-input:focus { border-color: #0A0A0A; }
        .contact-input::placeholder { color: #A1A1AA; }
      `}</style>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mono-label text-zinc-500">
        {label} {required && <span className="text-[#BE1E2D]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
